import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Plus, Power, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useConfig } from "@/hooks/useData";

function Field({ f, value, onChange, options }) {
  const base = "field-input";
  if (f.type === "boolean") return <input data-testid={`crud-field-${f.key}`} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-[#2563EB]" />;
  if (f.type === "select") return <select data-testid={`crud-field-${f.key}`} className={base} value={value ?? ""} onChange={(e) => onChange(e.target.value)}><option value="">—</option>{(options || f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>;
  if (f.type === "multiselect") {
    const vals = Array.isArray(value) ? value : [];
    return <select data-testid={`crud-field-${f.key}`} multiple className={`${base} !h-24`} value={vals} onChange={(e) => onChange([...e.target.selectedOptions].map((o) => o.value))}>{(options || f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>;
  }
  if (f.type === "tags") return <input data-testid={`crud-field-${f.key}`} className={base} value={Array.isArray(value) ? value.join(", ") : value || ""} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="comma, separated" />;
  if (f.type === "textarea") return <textarea data-testid={`crud-field-${f.key}`} className={`${base} !h-20 py-1`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  if (f.type === "number") return <input data-testid={`crud-field-${f.key}`} type="number" step="any" className={base} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} />;
  return <input data-testid={`crud-field-${f.key}`} type={f.type === "password" ? "password" : "text"} className={base} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} />;
}

export function CrudTable({ collection, title, description, fields, optionSources = {}, defaults = {} }) {
  const q = useConfig(collection);
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const rows = q.data || [];

  const optionsFor = (f, draft) => {
    const src = optionSources[f.key];
    if (!src) return f.options;
    return typeof src === "function" ? src(draft) : src;
  };
  const labelFor = (f, value, row) => {
    const opts = optionsFor(f, row) || [];
    if (f.type === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) return value.map((v) => opts.find((o) => o.value === v)?.label || v).join(", ");
    if (f.type === "select") return opts.find((o) => o.value === value)?.label || value || "—";
    if (f.type === "password") return "••••••";
    return value ?? "—";
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...editing };
      delete body.id;
      if (editing.id) await api.put(`/config/${collection}/${editing.id}`, body); else await api.post(`/config/${collection}`, body);
      toast.success(`${title}: saved`);
      qc.invalidateQueries({ queryKey: ["config", collection] });
      qc.invalidateQueries({ queryKey: ["board"] });
      setEditing(null);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const toggleActive = async (row) => {
    try { await api.put(`/config/${collection}/${row.id}`, { active: row.active === false }); qc.invalidateQueries({ queryKey: ["config", collection] }); qc.invalidateQueries({ queryKey: ["board"] }); toast.success(`${row.active === false ? "Activated" : "Deactivated"}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Permanently delete this ${title.toLowerCase().replace(/s$/, "")}? Prefer deactivating to keep history intact.`)) return;
    try { await api.delete(`/config/${collection}/${row.id}`); qc.invalidateQueries({ queryKey: ["config", collection] }); qc.invalidateQueries({ queryKey: ["board"] }); toast.success("Deleted"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const visible = useMemo(() => fields.filter((f) => !f.hideInTable), [fields]);

  return (
    <div className="space-y-3" data-testid={`crud-${collection}`}>
      <div className="flex items-center gap-2">
        <div><h2 className="display text-base font-semibold">{title}</h2>{description && <p className="text-xs text-muted-foreground">{description}</p>}</div>
        <button data-testid={`crud-${collection}-add`} onClick={() => setEditing({ ...defaults, active: true })} className="ml-auto h-8 px-3 rounded-md bg-primary text-primary-foreground text-xs font-semibold flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add</button>
      </div>
      {editing && (
        <div className="rounded-xl border bg-card p-3 grid grid-cols-3 gap-3" data-testid={`crud-${collection}-form`}>
          {fields.filter((f) => !f.readOnly).map((f) => (
            <label key={f.key} className={`block ${f.wide ? "col-span-3" : ""}`}>
              <span className="overline-label">{f.label}{f.required && " *"}</span>
              <div className="mt-0.5"><Field f={f} value={editing[f.key]} onChange={(v) => setEditing((e) => ({ ...e, [f.key]: v }))} options={optionsFor(f, editing)} /></div>
              {f.help && <span className="text-[10px] text-muted-foreground">{f.help}</span>}
            </label>
          ))}
          <div className="col-span-3 flex justify-end gap-2">
            <button data-testid={`crud-${collection}-cancel`} onClick={() => setEditing(null)} className="h-8 px-3 rounded-md border text-xs">Cancel</button>
            <button data-testid={`crud-${collection}-save`} disabled={busy} onClick={save} className="h-8 px-3 rounded-md bg-primary text-primary-foreground text-xs font-semibold">{editing.id ? "Save changes" : "Create"}</button>
          </div>
        </div>
      )}
      <div className="rounded-xl border overflow-auto">
        <table className="w-full text-xs text-left">
          <thead className="bg-muted/50 overline-label"><tr>{visible.map((f) => <th key={f.key} className="px-2 py-1.5 font-medium">{f.label}</th>)}<th className="px-2 py-1.5 font-medium">Active</th><th className="px-2 py-1.5" /></tr></thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} data-testid={`crud-${collection}-row-${row.id}`} className={`border-t ${row.active === false ? "opacity-50" : ""}`}>
                {visible.map((f) => <td key={f.key} className={`px-2 py-1 ${f.mono ? "mono" : ""}`}>{String(labelFor(f, row[f.key], row))}</td>)}
                <td className="px-2 py-1">{row.active === false ? <span className="chip border-slate-500/40">INACTIVE</span> : <span className="chip border-emerald-500/50 text-emerald-600 dark:text-emerald-300">ACTIVE</span>}</td>
                <td className="px-2 py-1 whitespace-nowrap text-right">
                  <button data-testid={`crud-${collection}-edit-${row.id}`} onClick={() => setEditing({ ...row })} className="p-1 rounded hover:bg-accent" title="Edit"><Pencil className="h-3.5 w-3.5" /></button>
                  <button data-testid={`crud-${collection}-toggle-${row.id}`} onClick={() => toggleActive(row)} className="p-1 rounded hover:bg-accent" title={row.active === false ? "Activate" : "Deactivate"}><Power className="h-3.5 w-3.5" /></button>
                  <button data-testid={`crud-${collection}-delete-${row.id}`} onClick={() => remove(row)} className="p-1 rounded hover:bg-accent text-red-500" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={visible.length + 2} className="px-2 py-4 text-center text-muted-foreground">No records.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
